import type {
	AdminRedemptionFilters,
	AdminRedemptionRow,
	ListReferralRedemptionsParams,
	ListReferralRewardsParams,
	ReferralReward,
	ReferralSettings,
	ReferralStats,
	ReferralStatsQuery,
} from "@/features/referral/api/types";
import { toAdminSearchParams } from "@/shared/api/admin-list";
import { api } from "@/shared/api/client";
import type { InfinityPage } from "@/shared/api/types";

export async function getReferralSettings(): Promise<ReferralSettings> {
	return api.get("admin/referrals/settings").json();
}

export async function updateReferralSettings(input: {
	isActive?: boolean;
	pointsPerReferral?: number;
	shareText?: string;
}): Promise<ReferralSettings> {
	return api.patch("admin/referrals/settings", { json: input }).json();
}

export async function listReferralRewards(
	params: ListReferralRewardsParams = {},
): Promise<InfinityPage<ReferralReward>> {
	return api
		.get("admin/referrals/rewards", {
			searchParams: toAdminSearchParams({
				...params,
				sort: params.sort ?? "pointsCost",
				order: params.order ?? "ASC",
			}),
		})
		.json();
}

export async function createReferralReward(body: {
	reward: string;
	pointsCost: number;
	isActive?: boolean;
}): Promise<ReferralReward> {
	return api.post("admin/referrals/rewards", { json: body }).json();
}

export async function updateReferralReward(
	id: string,
	body: { reward?: string; pointsCost?: number; isActive?: boolean },
): Promise<ReferralReward> {
	return api.patch(`admin/referrals/rewards/${id}`, { json: body }).json();
}

export async function deleteReferralReward(id: string): Promise<void> {
	await api.delete(`admin/referrals/rewards/${id}`);
}

export async function getReferralStats(
	query: ReferralStatsQuery,
): Promise<ReferralStats> {
	const search = new URLSearchParams();
	search.set("period", query.period);
	if (query.from) {
		search.set("from", query.from);
	}
	if (query.to) {
		search.set("to", query.to);
	}
	return api.get("admin/referrals/stats", { searchParams: search }).json();
}

export function toRedemptionSearchParams(
	params: ListReferralRedemptionsParams,
): URLSearchParams {
	const search = toAdminSearchParams({
		search: params.search,
		page: params.page,
		limit: params.limit,
		sort: params.sort ?? "createdAt",
		order: params.order ?? "DESC",
	});
	if (params.staffId) {
		search.set("staffId", params.staffId);
	}
	if (params.rewardName) {
		search.set("rewardName", params.rewardName);
	}
	if (params.from) {
		search.set("from", params.from);
	}
	if (params.to) {
		search.set("to", params.to);
	}
	return search;
}

export async function listReferralRedemptions(
	params: ListReferralRedemptionsParams = {},
): Promise<InfinityPage<AdminRedemptionRow>> {
	return api
		.get("admin/referrals/redemptions", {
			searchParams: toRedemptionSearchParams(params),
		})
		.json();
}

export async function getReferralRedemptionFilters(): Promise<AdminRedemptionFilters> {
	return api.get("admin/referrals/redemptions/filters").json();
}

export const referralKeys = {
	settings: ["admin-referrals", "settings"] as const,
	rewards: (params: ListReferralRewardsParams) =>
		["admin-referrals", "rewards", params] as const,
	stats: (query: ReferralStatsQuery) =>
		["admin-referral", "stats", query] as const,
	redemptions: (params: ListReferralRedemptionsParams) =>
		["admin-referrals", "redemptions", params] as const,
	redemptionFilters: ["admin-referrals", "redemption-filters"] as const,
};
